import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MonitorIcon, SmartphoneIcon, PaletteIcon, CodeIcon, SparklesIcon, ArrowRightIcon, ArrowLeftIcon, CheckCircle2Icon, MailIcon, LayoutIcon, RocketIcon, SearchIcon, PenToolIcon, ZapIcon, LockIcon, PlayIcon, XIcon, RefreshCwIcon, HandshakeIcon, UserCheckIcon, ShieldCheckIcon, TagIcon, InfoIcon } from 'lucide-react';
import { GlitterHeart } from '../../components/GlitterHeart';
import { SparkleEffect } from '../../components/SparkleEffect';
type ProjectType = 'website' | 'web-app' | 'mobile-app';
interface TourStep {
  title: string;
  description: string;
  // Position of the hotspot on the screenshot as percentages (0–100)
  x: number;
  y: number;
}
interface Project {
  id: string;
  name: string;
  type: ProjectType;
  description: string;
  tags: string[];
  status?: 'Live' | 'In Development' | 'Launching Soon';
  screenshot?: string;
  liveUrl?: string;
  accent: string;
  placeholderTitle: string;
  tour?: TourStep[];
}
const projects: Project[] = [{
  id: 'trippin-through-life-app',
  name: "Trippin' Through Life",
  type: 'web-app',
  description: 'An AI-powered life management app that builds personalized routines, schedules, and wellness check-ins — designed to turn chaos into structure.',
  tags: ['AI', 'Life Management', 'Routines', 'Wellness'],
  status: 'Live',
  screenshot: 'https://cdn.magicpatterns.com/uploads/jaQAgUHCArCHsgYVAj8THj/ChatGPT_Image_May_10,_2026,_02_38_15_PM.png',
  liveUrl: 'https://trippinthroughlife.base44.app',
  accent: 'from-plum-100 via-white to-cta-100',
  placeholderTitle: 'Life Management App',
  tour: [{
    title: 'Routines Builder',
    description: 'Morning, Night, and Weekly Reset flows that turn habits into momentum — step counts, durations, and progress bars built in.',
    x: 26,
    y: 26
  }, {
    title: 'AI Daily Schedule',
    description: 'A full day mapped minute-by-minute with energy levels, balanced day badges, and one-tap add for new events.',
    x: 75,
    y: 28
  }, {
    title: 'Me Time & Mood',
    description: 'Daily affirmations, mood check-ins, and quick-tap feelings — built-in nervous system support.',
    x: 25,
    y: 76
  }, {
    title: "Today's Focus",
    description: 'Personalized motivation, a short focus list, and an "Up Next" card so users always know the next step.',
    x: 76,
    y: 76
  }]
}, {
  id: 'gsf-agency',
  name: 'GSF Agency',
  type: 'website',
  description: 'A bold, cinematic brand site for a creator agency — atmospheric visuals, team profiles, live schedules, and dual login portals for agents and creators.',
  tags: ['Branding', 'Web Design', 'Agency'],
  status: 'Live',
  screenshot: 'https://cdn.magicpatterns.com/uploads/7FkFyNvJqhZyLqcviMqVWK/image.png',
  liveUrl: 'https://www.gfsliveagency.com',
  accent: 'from-purple-200 via-fuchsia-100 to-indigo-200',
  placeholderTitle: 'Agency Website',
  tour: [{
    title: 'Brand Identity',
    description: 'Custom GSF wordmark set against a starfield — a signature look that lands the agency\'s "reach for the stars" mission.',
    x: 10,
    y: 8
  }, {
    title: 'Clean Navigation',
    description: 'A clear path through Treatments, About, Team, Services, Schedules, and Join — built to convert curiosity into action.',
    x: 60,
    y: 8
  }, {
    title: 'Dual Login Portals',
    description: 'Separate Agent Login and Creator Login portals tucked neatly in the top-right for returning users.',
    x: 92,
    y: 8
  }, {
    title: 'Mission Statement',
    description: "A glassy, centered mission card so the brand's heart is the very first thing visitors read.",
    x: 50,
    y: 50
  }, {
    title: 'Primary CTAs',
    description: 'Two unmissable calls-to-action — "Contact Us" and "Join GSF" — for both prospects and creators ready to commit.',
    x: 50,
    y: 80
  }]
}, {
  id: 'wonders-of-life',
  name: 'Wonders of Life Wellness Spa',
  type: 'website',
  description: 'A serene, luxury wellness-spa site designed to feel like the experience itself. Soft lavender palette, atmospheric photography, and a beautifully animated treatment showcase.',
  tags: ['Wellness', 'Luxury', 'Brand Site'],
  status: 'Live',
  screenshot: 'https://cdn.magicpatterns.com/uploads/1xVZ4cruGbPcAxeoaZbD7W/image.png',
  liveUrl: 'https://project-wondersoflife.magicpatterns.app',
  accent: 'from-purple-100 via-white to-lavender-200',
  placeholderTitle: 'Wellness Spa Website',
  tour: [{
    title: 'Elegant Wordmark',
    description: 'A softly italicized "Wonders of Life" wordmark — luxurious without trying too hard.',
    x: 10,
    y: 6
  }, {
    title: 'Treatment Hotspots',
    description: "Floating labels — Bio Charging, Body Sculpting — guide the eye through the spa's most-loved services.",
    x: 24,
    y: 18
  }, {
    title: 'Anchor Identity',
    description: 'The lotus and Wonders of Life Wellness Spa identity centered behind the reception, grounding the entire scene.',
    x: 50,
    y: 36
  }, {
    title: 'More Treatments',
    description: 'Full Body Detox and Hydrotherapy bays visible at a glance — every service feels accessible.',
    x: 88,
    y: 18
  }, {
    title: 'Sticky Booking CTA',
    description: 'A "Book a Visit" button parked top-right — visible everywhere on the page so conversion is always one tap away.',
    x: 92,
    y: 6
  }]
}, {
  id: 'inner-intentions-app',
  name: 'Inner Intentions',
  type: 'mobile-app',
  description: 'A neon-noir social card game app for connection, chaos, and growth. Multiple game modes, custom decks, and a vibe that feels like late-night truth-telling with friends.',
  tags: ['Mobile', 'Social', 'Game', 'Neon'],
  status: 'Live',
  screenshot: 'https://cdn.magicpatterns.com/uploads/ha6E51brE895HJ3MVL5G7K/inner_intentions_resized_1280x720.png',
  liveUrl: 'https://innerintentions.base44.app',
  accent: 'from-fuchsia-300 via-purple-200 to-cyan-200',
  placeholderTitle: 'Social Card Game App',
  tour: [{
    title: 'Neon Brand Identity',
    description: 'A hand-painted neon "Inner Intentions" wordmark with a tagline that hooks instantly — "We grow here."',
    x: 50,
    y: 25
  }, {
    title: 'Speed Dating Deck',
    description: 'Fast-paced compatibility deck — first impressions and deeper connection prompts to break the ice quickly.',
    x: 28,
    y: 65
  }, {
    title: 'Hot Seat Mode',
    description: 'The main solo deck — relationship, spicy, red/green-flag, and growth questions for real conversation.',
    x: 43,
    y: 65
  }, {
    title: 'Chaos Mode',
    description: 'A funny, messy multiplayer party mode with wild red-and-green-flag chaos cards for game nights.',
    x: 58,
    y: 65
  }, {
    title: 'Solo Mode',
    description: 'Five personal insight decks for introspection, journaling prompts, and growth work — no group required.',
    x: 73,
    y: 65
  }, {
    title: 'Bottom Nav',
    description: 'A clean two-tab bottom bar — Home and Profile — with glowing neon active states.',
    x: 50,
    y: 95
  }]
}];
const offerings = [{
  icon: <MonitorIcon className="w-6 h-6" data-id="element-2751" />,
  title: 'Custom Websites',
  desc: 'Brand sites, portfolios, service businesses, course launches, blogs — built from scratch and built to convert.'
}, {
  icon: <SmartphoneIcon className="w-6 h-6" data-id="element-2752" />,
  title: 'Mobile & Web Apps',
  desc: 'iOS, Android, and full web apps designed with real user flows, real interactions, and real polish.'
}, {
  icon: <PaletteIcon className="w-6 h-6" data-id="element-2753" />,
  title: 'Branding & Design Systems',
  desc: 'Logos, color systems, typography, and component libraries that scale across every touchpoint.'
}, {
  icon: <CodeIcon className="w-6 h-6" data-id="element-2754" />,
  title: 'AI-Powered Build Assistance',
  desc: 'Fast, modern, AI-assisted development — without sacrificing taste or quality.'
}];
const process = [{
  icon: <SearchIcon className="w-5 h-5" data-id="element-2755" />,
  step: '01',
  title: 'Discover',
  desc: 'We dig into your brand, audience, and goals so the design actually fits your business.'
}, {
  icon: <PenToolIcon className="w-5 h-5" data-id="element-2756" />,
  step: '02',
  title: 'Design',
  desc: 'Wireframes, mockups, and a polished UI — refined together until it feels unmistakably you.'
}, {
  icon: <ZapIcon className="w-5 h-5" data-id="element-2757" />,
  step: '03',
  title: 'Build',
  desc: 'Fast, modern code. Mobile-first. Responsive. Built to load fast and convert visitors.'
}, {
  icon: <RocketIcon className="w-5 h-5" data-id="element-2758" />,
  step: '04',
  title: 'Launch',
  desc: 'Go live with confidence — plus optional support, updates, and growth iteration after launch.'
}];
const typeMeta: Record<ProjectType, {
  label: string;
  icon: React.ReactNode;
}> = {
  website: {
    label: 'Website',
    icon: <MonitorIcon className="w-3.5 h-3.5" data-id="element-2759" />
  },
  'web-app': {
    label: 'Web App',
    icon: <LayoutIcon className="w-3.5 h-3.5" data-id="element-2760" />
  },
  'mobile-app': {
    label: 'Mobile App',
    icon: <SmartphoneIcon className="w-3.5 h-3.5" data-id="element-2761" />
  }
};
function ProjectMockup({
  project
}: {
  project: Project;
}) {
  return <div className="relative rounded-2xl overflow-hidden shadow-xl border border-plum-100 bg-white" data-id="element-2762">
      {/* Browser chrome */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-plum-50 border-b border-plum-100" data-id="element-2763">
        <div className="flex items-center gap-1.5" data-id="element-2764">
          <span className="w-3 h-3 rounded-full bg-red-400/70" data-id="element-2765" />
          <span className="w-3 h-3 rounded-full bg-yellow-400/70" data-id="element-2766" />
          <span className="w-3 h-3 rounded-full bg-green-400/70" data-id="element-2767" />
        </div>
        <div className="flex-1 mx-3" data-id="element-2768">
          <div className="bg-white/80 rounded-md px-3 py-1 flex items-center gap-1.5 text-[10px] text-plum-400 font-mono" data-id="element-2769">
            <LockIcon className="w-2.5 h-2.5" data-id="element-2770" />
            <span className="truncate" data-id="element-2771">private preview</span>
          </div>
        </div>
      </div>

      {/* Screenshot */}
      <div className="aspect-video relative overflow-hidden" data-id="element-2772">
        {project.screenshot ? <img src={project.screenshot} alt={`${project.name} preview`} className="w-full h-full object-cover" data-id="element-2773" /> : <div className={`w-full h-full bg-gradient-to-br ${project.accent} flex items-center justify-center relative`} data-id="element-2774">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.6),transparent_50%)]" data-id="element-2775" />
            <div className="relative text-center px-6" data-id="element-2776">
              <SparklesIcon className="w-8 h-8 text-plum-400 mx-auto mb-2 opacity-50" data-id="element-2777" />
              <p className="font-heading text-lg font-bold text-plum-700" data-id="element-2778">
                {project.placeholderTitle}
              </p>
            </div>
          </div>}
      </div>
    </div>;
}
function TourModal({
  project,
  onClose
}: {
  project: Project;
  onClose: () => void;
}) {
  const [activeStep, setActiveStep] = useState(0);
  const totalSteps = project.tour?.length ?? 0;
  const next = useCallback(() => setActiveStep(s => Math.min(s + 1, totalSteps - 1)), [totalSteps]);
  const prev = useCallback(() => setActiveStep(s => Math.max(s - 1, 0)), []);
  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [onClose, next, prev]);
  if (!project.tour) return null;
  const step = project.tour[activeStep];
  const meta = typeMeta[project.type];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-[200] bg-plum-900/80 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 overflow-y-auto" onClick={onClose} data-id="element-2779">
      <motion.div initial={{
      opacity: 0,
      y: 20,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.97
    }} transition={{
      duration: 0.25
    }} onClick={e => e.stopPropagation()} className="w-full max-w-6xl bg-white rounded-3xl shadow-2xl overflow-hidden relative my-4" data-id="element-2780">
        {/* Close */}
        <button onClick={onClose} className="absolute top-4 right-4 z-30 p-2 rounded-full bg-white/90 backdrop-blur text-plum-600 hover:bg-white hover:text-plum-900 shadow-md transition-colors" aria-label="Close tour" data-id="element-2781">
          <XIcon className="w-5 h-5" data-id="element-2782" />
        </button>

        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-blush-100 pr-16" data-id="element-2783">
          <div className="flex items-center gap-2 flex-wrap mb-2" data-id="element-2784">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-plum-100 text-plum-700 text-xs font-bold" data-id="element-2785">
              {meta.icon}
              {meta.label}
            </span>
            {project.status && <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${project.status === 'Live' ? 'bg-emerald-100 text-emerald-700' : project.status === 'In Development' ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'}`} data-id="element-2786">
                {project.status === 'Live' && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" data-id="element-2787" />}
                {project.status}
              </span>}
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cta-100 text-cta-600 text-xs font-bold uppercase tracking-wider" data-id="element-2788">
              <SparklesIcon className="w-3 h-3" data-id="element-2789" />
              Interactive Tour
            </span>
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-plum-800" data-id="element-2790">
            {project.name}
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.6fr,1fr]" data-id="element-2791">
          {/* Screenshot with hotspot */}
          <div className="relative bg-plum-50" data-id="element-2792">
            <div className="relative aspect-video overflow-hidden" data-id="element-2793">
              {project.screenshot && <img src={project.screenshot} alt={`${project.name} preview`} className="w-full h-full object-cover" data-id="element-2794" />}
              {/* Dim overlay */}
              <div className="absolute inset-0 bg-plum-900/30" data-id="element-2795" />

              {/* Hotspot at current step */}
              <AnimatePresence mode="wait" data-id="element-2796">
                <motion.div key={activeStep} initial={{
                opacity: 0,
                scale: 0.6
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.6
              }} transition={{
                duration: 0.3
              }} className="absolute" style={{
                left: `${step.x}%`,
                top: `${step.y}%`,
                transform: 'translate(-50%, -50%)'
              }} data-id="element-2797">
                  {/* Pulsing rings */}
                  <div className="relative" data-id="element-2798">
                    <motion.div animate={{
                    scale: [1, 2, 1],
                    opacity: [0.5, 0, 0.5]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeOut'
                  }} className="absolute inset-0 w-10 h-10 -translate-x-1/2 -translate-y-1/2 rounded-full bg-cta-500" data-id="element-2799" />
                    <motion.div animate={{
                    scale: [1, 1.7, 1],
                    opacity: [0.7, 0, 0.7]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeOut',
                    delay: 0.4
                  }} className="absolute inset-0 w-10 h-10 -translate-x-1/2 -translate-y-1/2 rounded-full bg-cta-400" data-id="element-2800" />
                    {/* Core dot */}
                    <div className="relative w-10 h-10 -translate-x-1/2 -translate-y-1/2 rounded-full bg-cta-500 border-4 border-white shadow-xl flex items-center justify-center text-white font-heading font-bold text-sm" data-id="element-2801">
                      {activeStep + 1}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* All step indicators (small numbered dots) */}
              {project.tour.map((s, i) => {
              if (i === activeStep) return null;
              return <button key={i} onClick={() => setActiveStep(i)} className="absolute group" style={{
                left: `${s.x}%`,
                top: `${s.y}%`,
                transform: 'translate(-50%, -50%)'
              }} aria-label={`Jump to step ${i + 1}`} data-id="element-2802">
                    <div className="w-6 h-6 rounded-full bg-white/80 backdrop-blur-sm border-2 border-cta-500 flex items-center justify-center text-cta-600 font-bold text-xs shadow-md group-hover:scale-110 group-hover:bg-white transition-transform" data-id="element-2803">
                      {i + 1}
                    </div>
                  </button>;
            })}
            </div>
          </div>

          {/* Step description */}
          <div className="p-6 md:p-8 flex flex-col bg-white" data-id="element-2804">
            <div className="flex items-center justify-between mb-4" data-id="element-2805">
              <span className="text-xs font-bold text-plum-400 uppercase tracking-widest" data-id="element-2806">
                Step {activeStep + 1} of {totalSteps}
              </span>
              <div className="flex items-center gap-1" data-id="element-2807">
                {project.tour.map((_, i) => <button key={i} onClick={() => setActiveStep(i)} className={`h-1.5 rounded-full transition-all ${i === activeStep ? 'w-6 bg-cta-500' : 'w-1.5 bg-plum-200 hover:bg-plum-300'}`} aria-label={`Go to step ${i + 1}`} data-id="element-2808" />)}
              </div>
            </div>

            <AnimatePresence mode="wait" data-id="element-2809">
              <motion.div key={activeStep} initial={{
              opacity: 0,
              x: 12
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -12
            }} transition={{
              duration: 0.25
            }} className="flex-1" data-id="element-2810">
                <h3 className="font-heading text-2xl font-bold text-plum-800 mb-3" data-id="element-2811">
                  {step.title}
                </h3>
                <p className="text-plum-600 leading-relaxed" data-id="element-2812">
                  {step.description}
                </p>
              </motion.div>
            </AnimatePresence>

            {/* Controls */}
            <div className="flex items-center gap-2 mt-8 pt-6 border-t border-blush-100" data-id="element-2813">
              <button onClick={prev} disabled={activeStep === 0} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white border border-plum-200 text-plum-700 font-bold text-sm hover:bg-plum-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-2814">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-2815" />
                Prev
              </button>
              {activeStep < totalSteps - 1 ? <button onClick={next} className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-cta-500 text-white font-bold text-sm hover:bg-cta-600 transition-colors shadow-md shadow-cta-500/20" data-id="element-2816">
                  Next
                  <ArrowRightIcon className="w-4 h-4" data-id="element-2817" />
                </button> : <a href="https://calendly.com/trippinwithmandy/free-consultation-call" target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-cta-500 text-white font-bold text-sm hover:bg-cta-600 transition-colors shadow-md shadow-cta-500/20" data-id="element-2818">
                  <GlitterHeart size={14} variant="light" data-id="element-2819" />
                  Book a Call
                </a>}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}
function LivePreviewModal({
  project,
  onClose
}: {
  project: Project;
  onClose: () => void;
}) {
  const [iframeKey, setIframeKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const isMobile = project.type === 'mobile-app';
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [onClose]);
  if (!project.liveUrl) return null;
  const meta = typeMeta[project.type];
  const handleRefresh = () => {
    setLoading(true);
    setIframeKey(k => k + 1);
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="fixed inset-0 z-[200] bg-plum-900/85 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 overflow-y-auto" onClick={onClose} data-id="element-2820">
      <motion.div initial={{
      opacity: 0,
      y: 20,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.97
    }} transition={{
      duration: 0.25
    }} onClick={e => e.stopPropagation()} className="w-full max-w-6xl bg-white rounded-3xl shadow-2xl overflow-hidden relative my-4" data-id="element-2821">
        {/* Close */}
        <button onClick={onClose} className="absolute top-4 right-4 z-30 p-2 rounded-full bg-white/90 backdrop-blur text-plum-600 hover:bg-white hover:text-plum-900 shadow-md transition-colors" aria-label="Close preview" data-id="element-2822">
          <XIcon className="w-5 h-5" data-id="element-2823" />
        </button>

        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-blush-100 pr-16" data-id="element-2824">
          <div className="flex items-center gap-2 flex-wrap mb-2" data-id="element-2825">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-plum-100 text-plum-700 text-xs font-bold" data-id="element-2826">
              {meta.icon}
              {meta.label}
            </span>
            {project.status && <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${project.status === 'Live' ? 'bg-emerald-100 text-emerald-700' : project.status === 'In Development' ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'}`} data-id="element-2827">
                {project.status === 'Live' && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" data-id="element-2828" />}
                {project.status}
              </span>}
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cta-100 text-cta-600 text-xs font-bold uppercase tracking-wider" data-id="element-2829">
              <PlayIcon className="w-3 h-3 fill-cta-600" data-id="element-2830" />
              Live Interactive Demo
            </span>
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-plum-800" data-id="element-2831">
            {project.name}
          </h2>
          <p className="text-plum-500 text-sm font-medium mt-1" data-id="element-2832">
            Click around — this is the real thing, running live inside the page.
          </p>
        </div>

        {/* Preview area */}
        <div className="bg-gradient-to-br from-plum-50 to-blush-50 p-4 sm:p-8" data-id="element-2833">
          {isMobile ? <div className="flex justify-center" data-id="element-2834">
              {/* Phone frame */}
              <div className="relative w-full max-w-[360px]" data-id="element-2835">
                <div className="relative rounded-[3rem] bg-plum-900 p-3 shadow-2xl" data-id="element-2836">
                  {/* Notch */}
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 w-28 h-6 bg-plum-900 rounded-b-2xl z-10" data-id="element-2837" />
                  {/* Screen */}
                  <div className="relative rounded-[2.25rem] overflow-hidden bg-black aspect-[9/19.5]" data-id="element-2838">
                    {loading && <div className="absolute inset-0 flex items-center justify-center bg-plum-900 z-10" data-id="element-2839">
                        <div className="text-center" data-id="element-2840">
                          <div className="w-8 h-8 border-2 border-cta-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" data-id="element-2841" />
                          <p className="text-white/70 text-xs font-medium" data-id="element-2842">
                            Loading live demo…
                          </p>
                        </div>
                      </div>}
                    <iframe key={iframeKey} src={project.liveUrl} title={`${project.name} live preview`} onLoad={() => setLoading(false)} sandbox="allow-scripts allow-same-origin allow-forms allow-popups" referrerPolicy="no-referrer" className="w-full h-full border-0 bg-white" data-id="element-2843" />
                  </div>
                </div>
                {/* Refresh button below phone */}
                <div className="flex justify-center mt-4" data-id="element-2844">
                  <button onClick={handleRefresh} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-plum-700 font-bold text-xs border border-plum-200 hover:bg-plum-50 transition-colors shadow-sm" data-id="element-2845">
                    <RefreshCwIcon className="w-3.5 h-3.5" data-id="element-2846" />
                    Restart Demo
                  </button>
                </div>
              </div>
            </div> : <div className="rounded-2xl overflow-hidden shadow-xl border border-plum-100 bg-white" data-id="element-2847">
              {/* Browser chrome */}
              <div className="flex items-center gap-2 px-4 py-2.5 bg-plum-50 border-b border-plum-100" data-id="element-2848">
                <div className="flex items-center gap-1.5" data-id="element-2849">
                  <span className="w-3 h-3 rounded-full bg-red-400/70" data-id="element-2850" />
                  <span className="w-3 h-3 rounded-full bg-yellow-400/70" data-id="element-2851" />
                  <span className="w-3 h-3 rounded-full bg-green-400/70" data-id="element-2852" />
                </div>
                <div className="flex-1 mx-3" data-id="element-2853">
                  <div className="bg-white/80 rounded-md px-3 py-1 flex items-center gap-1.5 text-[11px] text-plum-400 font-mono" data-id="element-2854">
                    <LockIcon className="w-2.5 h-2.5" data-id="element-2855" />
                    <span className="truncate" data-id="element-2856">private preview</span>
                  </div>
                </div>
                <button onClick={handleRefresh} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white text-plum-700 font-bold text-[11px] border border-plum-200 hover:bg-plum-50 transition-colors" aria-label="Refresh demo" data-id="element-2857">
                  <RefreshCwIcon className="w-3 h-3" data-id="element-2858" />
                  Refresh
                </button>
              </div>
              {/* Iframe area */}
              <div className="relative w-full bg-white" style={{
            height: 'min(70vh, 640px)'
          }} data-id="element-2859">
                {loading && <div className="absolute inset-0 flex items-center justify-center bg-plum-50 z-10" data-id="element-2860">
                    <div className="text-center" data-id="element-2861">
                      <div className="w-8 h-8 border-2 border-cta-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" data-id="element-2862" />
                      <p className="text-plum-500 text-sm font-medium" data-id="element-2863">
                        Loading live demo…
                      </p>
                    </div>
                  </div>}
                <iframe key={iframeKey} src={project.liveUrl} title={`${project.name} live preview`} onLoad={() => setLoading(false)} sandbox="allow-scripts allow-same-origin allow-forms allow-popups" referrerPolicy="no-referrer" className="w-full h-full border-0" data-id="element-2864" />
              </div>
            </div>}

          <p className="text-center text-plum-400 text-xs font-medium mt-5 flex items-center justify-center gap-1.5" data-id="element-2865">
            <LockIcon className="w-3 h-3" data-id="element-2866" />
            Real, live demo — URL kept private. Like what you see?{' '}
            <a href="https://calendly.com/trippinwithmandy/free-consultation-call" target="_blank" rel="noopener noreferrer" className="text-cta-500 hover:text-cta-600 font-bold underline" data-id="element-2867">
              Book a call
            </a>
            .
          </p>
        </div>
      </motion.div>
    </motion.div>;
}
export function WebAppDesignPage() {
  const [filter, setFilter] = useState<'all' | ProjectType>('all');
  const [tourProject, setTourProject] = useState<Project | null>(null);
  const [livePreviewProject, setLivePreviewProject] = useState<Project | null>(null);
  const filteredProjects = filter === 'all' ? projects : projects.filter(p => p.type === filter);
  return <div className="min-h-screen bg-gradient-to-b from-blush-50 via-white to-lavender-50 pt-28 pb-24 relative overflow-hidden" data-id="element-2868">
      <SparkleEffect count={70} data-id="element-2869" />

      {/* Decorative blobs */}
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-blush-300/20 rounded-full blur-[120px] -z-10 -translate-x-1/3" data-id="element-2870" />
      <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-lavender-300/20 rounded-full blur-[120px] -z-10 translate-x-1/3" data-id="element-2871" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-2872">
        {/* HERO */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7
      }} className="text-center mb-20" data-id="element-2873">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-md border border-blush-200 text-sm font-bold text-plum-700 mb-6 shadow-sm uppercase tracking-wider" data-id="element-2874">
            <CodeIcon className="w-4 h-4" data-id="element-2875" />
            Web & App Design Studio
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-[1.05] tracking-tight" style={{
          fontFamily: '"Dancing Script", cursive'
        }} data-id="element-2876">
            <span className="block text-plum-800" data-id="element-2877">Websites & Apps,</span>
            <span className="block text-[#D63385] mt-2" data-id="element-2878">
              designed with soul.
            </span>
          </h1>

          <p className="text-xl text-plum-600 max-w-2xl mx-auto font-medium leading-relaxed mb-10" data-id="element-2879">
            Custom-built, conversion-focused websites and apps for creators,
            coaches, and small businesses. Beautiful. Fast. Real.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3" data-id="element-2880">
            <a href="https://calendly.com/trippinwithmandy/free-consultation-call" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-cta-500 text-white font-bold text-base hover:bg-cta-600 transition-all shadow-xl shadow-cta-500/30 hover:-translate-y-0.5" data-id="element-2881">
              <GlitterHeart size={18} variant="light" data-id="element-2882" />
              Book a Free Consultation
              <ArrowRightIcon className="w-4 h-4" data-id="element-2883" />
            </a>
            <a href="mailto:trippinwithmandy@gmail.com?subject=Web%20%26%20App%20Design%20Inquiry" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-plum-700 font-bold text-base border border-plum-200 hover:bg-plum-50 transition-all" data-id="element-2884">
              <MailIcon className="w-4 h-4" data-id="element-2885" />
              Email Me
            </a>
          </div>
        </motion.div>

        {/* WHAT I BUILD */}
        <section className="mb-24" data-id="element-2886">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="font-heading text-3xl md:text-4xl font-bold text-plum-800 text-center mb-12" data-id="element-2887">
            What I Build
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-2888">
            {offerings.map((item, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.08
          }} className="bg-white rounded-3xl p-7 border border-blush-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all" data-id="element-2889">
                <div className="w-12 h-12 rounded-xl bg-blush-100 flex items-center justify-center text-cta-500 mb-4" data-id="element-2890">
                  {item.icon}
                </div>
                <h3 className="font-heading text-xl font-bold text-plum-800 mb-2" data-id="element-2891">
                  {item.title}
                </h3>
                <p className="text-plum-600 leading-relaxed text-sm" data-id="element-2892">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </section>

        {/* PORTFOLIO */}
        <section className="mb-24" data-id="element-2893">
          <div className="text-center mb-10" data-id="element-2894">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="font-heading text-3xl md:text-4xl font-bold text-plum-800 mb-3" data-id="element-2895">
              Recent Work
            </motion.h2>
            <p className="text-plum-500 font-medium" data-id="element-2896">
              Tap any project to take an interactive feature tour — no live URL
              needed.
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-2 mb-12" data-id="element-2897">
            {[{
            id: 'all' as const,
            label: 'All'
          }, {
            id: 'website' as const,
            label: 'Websites'
          }, {
            id: 'web-app' as const,
            label: 'Web Apps'
          }, {
            id: 'mobile-app' as const,
            label: 'Mobile Apps'
          }].map(tab => <button key={tab.id} onClick={() => setFilter(tab.id)} className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${filter === tab.id ? 'bg-plum-800 text-white shadow-md' : 'bg-white text-plum-600 border border-plum-200 hover:bg-plum-50'}`} data-id="element-2898">
                {tab.label}
              </button>)}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2899">
            {filteredProjects.map((project, idx) => {
            const meta = typeMeta[project.type];
            const hasLive = !!project.liveUrl;
            const hasTour = !!project.tour && project.tour.length > 0;
            const isInteractive = hasLive || hasTour;
            const handleOpen = () => {
              if (hasLive) setLivePreviewProject(project);else if (hasTour) setTourProject(project);
            };
            return <motion.div key={project.id} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: idx * 0.08
            }} className="bg-white rounded-3xl p-6 md:p-7 border border-blush-100 shadow-sm hover:shadow-lg transition-shadow" data-id="element-2900">
                  {/* Mockup — clickable to launch live preview or tour */}
                  <button onClick={handleOpen} disabled={!isInteractive} className="block w-full group relative mb-6 rounded-2xl overflow-hidden focus:outline-none focus-visible:ring-4 focus-visible:ring-cta-300 disabled:cursor-default" aria-label={hasLive ? `Open live preview for ${project.name}` : hasTour ? `Open interactive tour for ${project.name}` : project.name} data-id="element-2901">
                    <ProjectMockup project={project} data-id="element-2902" />
                    {isInteractive && <div className="absolute inset-0 bg-plum-900/0 group-hover:bg-plum-900/40 transition-colors duration-300 flex items-center justify-center" data-id="element-2903">
                        <div className="opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-plum-800 font-bold text-sm shadow-2xl" data-id="element-2904">
                          <PlayIcon className="w-4 h-4 text-cta-500 fill-cta-500" data-id="element-2905" />
                          {hasLive ? 'Open Live Preview' : 'Take the Tour'}
                          {hasTour && !hasLive && <span className="text-plum-400 font-medium text-xs" data-id="element-2906">
                              · {project.tour!.length} steps
                            </span>}
                          {hasLive && <span className="text-plum-400 font-medium text-xs" data-id="element-2907">
                              · click around
                            </span>}
                        </div>
                      </div>}
                  </button>

                  {/* Meta */}
                  <div className="flex items-center gap-2 flex-wrap mb-3" data-id="element-2908">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-plum-100 text-plum-700 text-xs font-bold" data-id="element-2909">
                      {meta.icon}
                      {meta.label}
                    </span>
                    {project.status && <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${project.status === 'Live' ? 'bg-emerald-100 text-emerald-700' : project.status === 'In Development' ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'}`} data-id="element-2910">
                        {project.status === 'Live' && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" data-id="element-2911" />}
                        {project.status}
                      </span>}
                    {hasLive ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cta-100 text-cta-600 text-xs font-bold" data-id="element-2912">
                        <PlayIcon className="w-3 h-3 fill-cta-600" data-id="element-2913" />
                        Live Demo
                      </span> : hasTour ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cta-100 text-cta-600 text-xs font-bold" data-id="element-2914">
                        <SparklesIcon className="w-3 h-3" data-id="element-2915" />
                        Interactive Tour
                      </span> : null}
                  </div>

                  <h3 className="font-heading text-xl font-bold text-plum-800 mb-2" data-id="element-2916">
                    {project.name}
                  </h3>
                  <p className="text-plum-600 text-sm leading-relaxed mb-4" data-id="element-2917">
                    {project.description}
                  </p>

                  <div className="flex flex-wrap gap-1.5 mb-5" data-id="element-2918">
                    {project.tags.map(tag => <span key={tag} className="text-[11px] font-bold text-plum-500 bg-blush-50 px-2.5 py-1 rounded-md uppercase tracking-wide" data-id="element-2919">
                        {tag}
                      </span>)}
                  </div>

                  {isInteractive && <button onClick={handleOpen} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-plum-800 text-white font-bold text-sm hover:bg-plum-900 transition-colors" data-id="element-2920">
                      <PlayIcon className="w-4 h-4 fill-white" data-id="element-2921" />
                      {hasLive ? 'Open Live Preview' : 'Take the Tour'}
                    </button>}
                </motion.div>;
          })}
          </div>

          {/* Privacy note */}
          <p className="text-center text-plum-400 text-xs font-medium mt-10 max-w-md mx-auto flex items-center justify-center gap-1.5" data-id="element-2922">
            <LockIcon className="w-3 h-3" data-id="element-2923" />
            Client URLs are kept private. Live links shared on request after a
            consultation.
          </p>
        </section>

        {/* PROCESS */}
        <section className="mb-24" data-id="element-2924">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="font-heading text-3xl md:text-4xl font-bold text-plum-800 text-center mb-12" data-id="element-2925">
            The Process
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-2926">
            {process.map((step, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="bg-white rounded-3xl p-6 border border-blush-100 shadow-sm relative" data-id="element-2927">
                <div className="text-5xl font-heading font-bold text-blush-100 absolute top-2 right-4 select-none" data-id="element-2928">
                  {step.step}
                </div>
                <div className="relative" data-id="element-2929">
                  <div className="w-11 h-11 rounded-xl bg-cta-100 flex items-center justify-center text-cta-500 mb-4" data-id="element-2930">
                    {step.icon}
                  </div>
                  <h3 className="font-heading text-lg font-bold text-plum-800 mb-2" data-id="element-2931">
                    {step.title}
                  </h3>
                  <p className="text-plum-600 text-sm leading-relaxed" data-id="element-2932">
                    {step.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </section>

        {/* RESPONSIBILITIES — How It Works */}
        <section className="mb-24" data-id="element-2933">
          <div className="text-center mb-12" data-id="element-2934">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-md border border-blush-200 text-xs font-bold text-plum-700 mb-4 uppercase tracking-wider" data-id="element-2935">
              <HandshakeIcon className="w-3.5 h-3.5" data-id="element-2936" />
              How We'll Work Together
            </motion.div>
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="font-heading text-3xl md:text-4xl font-bold text-plum-800 mb-3" data-id="element-2937">
              Who Handles What
            </motion.h2>
            <p className="text-plum-500 font-medium max-w-2xl mx-auto" data-id="element-2938">
              I want us on the same page from day one. Here's exactly what I'll
              take care of — and what I'll need from you.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6" data-id="element-2939">
            {/* What I'll Handle */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-white rounded-3xl p-7 border border-blush-100 shadow-sm" data-id="element-2940">
              <div className="flex items-center gap-3 mb-5" data-id="element-2941">
                <div className="w-11 h-11 rounded-xl bg-cta-100 flex items-center justify-center text-cta-500" data-id="element-2942">
                  <CodeIcon className="w-5 h-5" data-id="element-2943" />
                </div>
                <h3 className="font-heading text-xl font-bold text-plum-800" data-id="element-2944">
                  What I'll Handle
                </h3>
              </div>
              <ul className="space-y-3" data-id="element-2945">
                {['App and website design from concept to launch', 'Building the features and functionality we discuss', 'Testing before submission or go-live', 'Google Play & Apple App Store submission assistance', 'Agreed-upon revisions during development', 'Regular updates so you always know where we are'].map(item => <li key={item} className="flex items-start gap-2.5 text-plum-700 text-sm leading-relaxed" data-id="element-2946">
                    <CheckCircle2Icon className="w-4 h-4 text-cta-500 shrink-0 mt-0.5" data-id="element-2947" />
                    <span data-id="element-2948">{item}</span>
                  </li>)}
              </ul>
            </motion.div>

            {/* What You'll Provide */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white rounded-3xl p-7 border border-blush-100 shadow-sm" data-id="element-2949">
              <div className="flex items-center gap-3 mb-5" data-id="element-2950">
                <div className="w-11 h-11 rounded-xl bg-plum-100 flex items-center justify-center text-plum-700" data-id="element-2951">
                  <UserCheckIcon className="w-5 h-5" data-id="element-2952" />
                </div>
                <h3 className="font-heading text-xl font-bold text-plum-800" data-id="element-2953">
                  What You'll Provide
                </h3>
              </div>
              <ul className="space-y-3" data-id="element-2954">
                {['A Google Play Developer account (for Android apps)', 'An Apple Developer account (for iPhone apps)', 'Logos, images, branding, and content to include', 'Feedback and approval throughout the process', 'Your vision for how the app should function and which features matter most'].map(item => <li key={item} className="flex items-start gap-2.5 text-plum-700 text-sm leading-relaxed" data-id="element-2955">
                    <CheckCircle2Icon className="w-4 h-4 text-plum-500 shrink-0 mt-0.5" data-id="element-2956" />
                    <span data-id="element-2957">{item}</span>
                  </li>)}
              </ul>
            </motion.div>
          </div>

          {/* Why developer accounts in your name */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-gradient-to-br from-blush-50 to-lavender-50 rounded-3xl p-7 md:p-9 border border-blush-200 shadow-sm" data-id="element-2958">
            <div className="flex items-start gap-4" data-id="element-2959">
              <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center text-cta-500 shrink-0 shadow-sm" data-id="element-2960">
                <ShieldCheckIcon className="w-6 h-6" data-id="element-2961" />
              </div>
              <div className="flex-1" data-id="element-2962">
                <h3 className="font-heading text-xl md:text-2xl font-bold text-plum-800 mb-3" data-id="element-2963">
                  Why your developer accounts need to be in your name
                </h3>
                <div className="space-y-3 text-plum-700 leading-relaxed text-sm md:text-base" data-id="element-2964">
                  <p data-id="element-2965">
                    Your app should be published under <strong data-id="element-2966">your</strong>{' '}
                    Google Play and Apple Developer accounts so that{' '}
                    <strong data-id="element-2967">you</strong> remain the owner. This gives you
                    complete control over your application, reviews, downloads,
                    updates, and store listings.
                  </p>
                  <p data-id="element-2968">
                    If an app is published under a developer's personal account
                    instead, the client can run into problems later. If the
                    developer stops offering services, closes their business,
                    loses access to their account, or there's a disagreement,
                    the client may have difficulty accessing or managing their
                    own app.
                  </p>
                  <p data-id="element-2969">
                    Publishing under your own accounts protects your ownership
                    and ensures your business stays in control of its app at all
                    times. I'll guide you through setting up the accounts and
                    you can add me as a developer so I can build, upload, and
                    maintain the app for you — while you stay the owner.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* PRICING */}
        <section className="mb-24" data-id="element-2970">
          <div className="text-center mb-12" data-id="element-2971">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-md border border-blush-200 text-xs font-bold text-plum-700 mb-4 uppercase tracking-wider" data-id="element-2972">
              <TagIcon className="w-3.5 h-3.5" data-id="element-2973" />
              Project Pricing
            </motion.div>
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="font-heading text-3xl md:text-4xl font-bold text-plum-800 mb-3" data-id="element-2974">
              Honest, transparent pricing
            </motion.h2>
            <p className="text-plum-500 font-medium max-w-2xl mx-auto" data-id="element-2975">
              Pricing is based on the complexity of the project and the number
              of features requested. Every quote is custom — these are starting
              points.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-2976">
            {/* Website Packages */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-white rounded-3xl p-7 md:p-8 border border-blush-100 shadow-sm" data-id="element-2977">
              <div className="flex items-center gap-3 mb-6" data-id="element-2978">
                <div className="w-11 h-11 rounded-xl bg-blush-100 flex items-center justify-center text-cta-500" data-id="element-2979">
                  <MonitorIcon className="w-5 h-5" data-id="element-2980" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-plum-800" data-id="element-2981">
                  Website Packages
                </h3>
              </div>
              <ul className="space-y-3" data-id="element-2982">
                {[{
                tier: 'Basic Website',
                price: 'Starting at $300',
                note: 'Clean, polished, conversion-ready essentials.'
              }, {
                tier: 'Advanced Website',
                price: 'Starting at $500',
                note: 'Multi-page site with custom design and integrations.'
              }, {
                tier: 'Premium Website',
                price: 'Starting at $1,000+',
                note: 'Full custom build with advanced features and animations.',
                highlight: true
              }].map(p => <li key={p.tier} className={`flex items-center justify-between gap-4 p-4 rounded-2xl border ${p.highlight ? 'bg-gradient-to-br from-cta-50 to-blush-50 border-cta-200' : 'bg-plum-50/50 border-plum-100'}`} data-id="element-2983">
                    <div className="min-w-0" data-id="element-2984">
                      <p className="font-heading font-bold text-plum-800 text-base" data-id="element-2985">
                        {p.tier}
                      </p>
                      <p className="text-plum-500 text-xs font-medium mt-0.5" data-id="element-2986">
                        {p.note}
                      </p>
                    </div>
                    <span className={`shrink-0 font-bold text-sm px-3 py-1.5 rounded-full ${p.highlight ? 'bg-cta-500 text-white' : 'bg-white text-plum-700 border border-plum-200'}`} data-id="element-2987">
                      {p.price}
                    </span>
                  </li>)}
              </ul>
            </motion.div>

            {/* Mobile App Packages */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white rounded-3xl p-7 md:p-8 border border-blush-100 shadow-sm" data-id="element-2988">
              <div className="flex items-center gap-3 mb-6" data-id="element-2989">
                <div className="w-11 h-11 rounded-xl bg-lavender-100 flex items-center justify-center text-plum-700" data-id="element-2990">
                  <SmartphoneIcon className="w-5 h-5" data-id="element-2991" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-plum-800" data-id="element-2992">
                  Mobile App Packages
                </h3>
              </div>
              <ul className="space-y-3" data-id="element-2993">
                {[{
                tier: 'Basic App',
                price: 'Starting at $300',
                note: 'Simple, focused app with core functionality.'
              }, {
                tier: 'Standard App',
                price: 'Starting at $500',
                note: 'Multi-feature app with custom UI and user flows.'
              }, {
                tier: 'Premium App',
                price: 'Starting at $1,000+',
                note: 'Advanced app with integrations, accounts, and polish.',
                highlight: true
              }].map(p => <li key={p.tier} className={`flex items-center justify-between gap-4 p-4 rounded-2xl border ${p.highlight ? 'bg-gradient-to-br from-cta-50 to-blush-50 border-cta-200' : 'bg-plum-50/50 border-plum-100'}`} data-id="element-2994">
                    <div className="min-w-0" data-id="element-2995">
                      <p className="font-heading font-bold text-plum-800 text-base" data-id="element-2996">
                        {p.tier}
                      </p>
                      <p className="text-plum-500 text-xs font-medium mt-0.5" data-id="element-2997">
                        {p.note}
                      </p>
                    </div>
                    <span className={`shrink-0 font-bold text-sm px-3 py-1.5 rounded-full ${p.highlight ? 'bg-cta-500 text-white' : 'bg-white text-plum-700 border border-plum-200'}`} data-id="element-2998">
                      {p.price}
                    </span>
                  </li>)}
              </ul>
            </motion.div>
          </div>

          {/* Pricing notes / deposit policy */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-white rounded-3xl p-7 md:p-8 border border-blush-100 shadow-sm" data-id="element-2999">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-3000">
              <div className="flex items-start gap-3" data-id="element-3001">
                <div className="w-10 h-10 rounded-xl bg-blush-100 flex items-center justify-center text-cta-500 shrink-0" data-id="element-3002">
                  <InfoIcon className="w-5 h-5" data-id="element-3003" />
                </div>
                <div data-id="element-3004">
                  <p className="font-heading font-bold text-plum-800 mb-1" data-id="element-3005">
                    Scope-based pricing
                  </p>
                  <p className="text-plum-600 text-sm leading-relaxed" data-id="element-3006">
                    Additional features, custom functionality, integrations, or
                    extensive revisions may increase the final cost.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-3007">
                <div className="w-10 h-10 rounded-xl bg-blush-100 flex items-center justify-center text-cta-500 shrink-0" data-id="element-3008">
                  <CheckCircle2Icon className="w-5 h-5" data-id="element-3009" />
                </div>
                <div data-id="element-3010">
                  <p className="font-heading font-bold text-plum-800 mb-1" data-id="element-3011">
                    Quoted before work begins
                  </p>
                  <p className="text-plum-600 text-sm leading-relaxed" data-id="element-3012">
                    After we discuss your needs, I'll send a quote outlining
                    exactly what's included so there are no surprises.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-3013">
                <div className="w-10 h-10 rounded-xl bg-blush-100 flex items-center justify-center text-cta-500 shrink-0" data-id="element-3014">
                  <TagIcon className="w-5 h-5" data-id="element-3015" />
                </div>
                <div data-id="element-3016">
                  <p className="font-heading font-bold text-plum-800 mb-1" data-id="element-3017">
                    Deposit & final payment
                  </p>
                  <p className="text-plum-600 text-sm leading-relaxed" data-id="element-3018">
                    A deposit is required before development starts, with the
                    remaining balance due upon project completion.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* WHO IT'S FOR */}
        <section className="mb-24" data-id="element-3019">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-gradient-to-br from-plum-900 via-plum-800 to-plum-900 rounded-[3rem] p-10 md:p-14 relative overflow-hidden" data-id="element-3020">
            <div className="absolute top-0 right-0 w-64 h-64 bg-cta-500/20 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3" data-id="element-3021" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-blush-500/20 rounded-full blur-3xl translate-y-1/3 -translate-x-1/3" data-id="element-3022" />
            <SparkleEffect count={30} data-id="element-3023" />

            <div className="relative z-10 max-w-3xl" data-id="element-3024">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-5" data-id="element-3025">
                Built for people who want{' '}
                <span className="text-cta-400" data-id="element-3026">more than a template.</span>
              </h2>
              <p className="text-plum-200 text-lg mb-8 leading-relaxed" data-id="element-3027">
                Whether you're launching a coaching brand, a course, a service
                business, an app, or a community — you deserve a site that
                actually feels like you. No cookie-cutter, no fluff, no AI slop.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-3028">
                {['Coaches & creators', 'Wellness brands', 'Course launches', 'Service businesses', 'App founders', 'Community platforms'].map(item => <div key={item} className="flex items-center gap-2.5" data-id="element-3029">
                    <CheckCircle2Icon className="w-5 h-5 text-cta-400 shrink-0" data-id="element-3030" />
                    <span className="text-white font-medium" data-id="element-3031">{item}</span>
                  </div>)}
              </div>
            </div>
          </motion.div>
        </section>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center bg-white rounded-3xl p-10 md:p-14 border border-blush-100 shadow-sm" data-id="element-3032">
          <div className="flex justify-center mb-4" data-id="element-3033">
            <GlitterHeart size={36} data-id="element-3034" />
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-plum-800 mb-4" data-id="element-3035">
            Ready to build something real?
          </h2>
          <p className="text-plum-600 text-lg mb-8 max-w-xl mx-auto font-medium" data-id="element-3036">
            Tell me about your project. Free consultation, no pressure, no
            cookie-cutter pitch.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3" data-id="element-3037">
            <a href="https://calendly.com/trippinwithmandy/free-consultation-call" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-cta-500 text-white font-bold text-base hover:bg-cta-600 transition-all shadow-xl shadow-cta-500/30 hover:-translate-y-0.5" data-id="element-3038">
              <GlitterHeart size={18} variant="light" data-id="element-3039" />
              Book a Free Call
              <ArrowRightIcon className="w-4 h-4" data-id="element-3040" />
            </a>
            <a href="mailto:trippinwithmandy@gmail.com?subject=Web%20%26%20App%20Design%20Inquiry" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-plum-700 font-bold text-base border border-plum-200 hover:bg-plum-50 transition-all" data-id="element-3041">
              <MailIcon className="w-4 h-4" data-id="element-3042" />
              trippinwithmandy@gmail.com
            </a>
          </div>
        </motion.div>
      </div>

      {/* Tour Modal */}
      <AnimatePresence data-id="element-3043">
        {tourProject && <TourModal project={tourProject} onClose={() => setTourProject(null)} data-id="element-3044" />}
      </AnimatePresence>

      {/* Live Preview Modal */}
      <AnimatePresence data-id="element-3045">
        {livePreviewProject && <LivePreviewModal project={livePreviewProject} onClose={() => setLivePreviewProject(null)} data-id="element-3046" />}
      </AnimatePresence>
    </div>;
}